import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { AppLayout, StickyFooter } from '@/components/layout';
import {
  UploadDropzone,
  PrivacyNotice,
  StagedFileCard,
} from '@/components/upload';
import { useFileStaging } from '@/hooks/useFileStaging';
import { useSessionStore } from '@/store/useSessionStore'; 
import { ROUTES } from '@/constants/routes';
import { kioskService } from '@/api/services/index';
import { decryptKioskToken } from '@/utils/kioskToken';
import type { KioskInfo } from '@/types/kiosk.types';

export function UploadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { stageFile, clear } = useFileStaging();

  const kiosk = useSessionStore((s) => s.kiosk);
  const setKiosk = useSessionStore((s) => s.setKiosk);

  const uploadedFile = useSessionStore((s) => s.uploadedFile);
  const pageInfo = useSessionStore((s) => s.pageInfo);
  const isDetectingPageCount = useSessionStore((s) => s.isDetectingPageCount);

  const [isInitializingKiosk, setIsInitializingKiosk] = useState(true);
  const [kioskError, setKioskError] = useState<string | null>(null);
  const setKioskToken = useSessionStore((s) => s.setKioskToken);
 

useEffect(() => {
  const initializeKiosk = async () => {
    const token = searchParams.get('token') ?? useSessionStore.getState().kioskToken;

    console.log('KIOSK TOKEN:', token);

    if (!token) {
      setKiosk(null);
      setKioskError('This kiosk link is missing its token.');
      setIsInitializingKiosk(false);
      return;
    }

    try {
      setIsInitializingKiosk(true);
      setKioskError(null);

      console.log('Starting kiosk token decryption...');

      const kioskId = await decryptKioskToken(token);

      console.log('DECRYPTED KIOSK ID:', kioskId);
      console.log('Calling kiosk API...');

      const response = await kioskService.getById(kioskId);

      console.log('KIOSK API RESPONSE:', response.data);

      if (!response.data.isStatus) {
        throw new Error(response.data.message || 'Unable to fetch kiosk');
      }

      const kiosk = response.data.data;

      if (!kiosk) {
        throw new Error('Kiosk not found');
      }

      const kioskInfo: KioskInfo = {
        kioskId: String(kiosk.id),
        label: kiosk.name,
        location: kiosk.location,
        status: kiosk.is_active ? 'online' : 'offline',

        vendorName: kiosk.vendor_name,
        printerModel: kiosk.printer_model,
        printerSpecification: kiosk.printer_specification,

        supportsColor: kiosk.is_supports_color,
        supportsDuplex: kiosk.is_supports_duplex,

        supportsA4: kiosk.is_a4,
        supportsLetter: kiosk.is_letter,
        supportsLegal: kiosk.is_legal,

        a4Count: kiosk.a4_count,
        letterCount: kiosk.letter_count,
        legalCount: kiosk.legal_count,

        vendorId: kiosk.vendor,

        bwRatePerPage: kiosk.rate_charge
          ? parseFloat(kiosk.rate_charge.bw_rate_per_page)
          : null,
        colorRatePerPage: kiosk.rate_charge
          ? parseFloat(kiosk.rate_charge.color_rate_per_page)
          : null,
        duplexExtraCharge: kiosk.rate_charge
          ? parseFloat(kiosk.rate_charge.duplex_extra_charge)
          : null,
        // `|| 0` keeps a missing/blank fee from turning into NaN
        merchantFees: kiosk.rate_charge ? parseFloat(kiosk.rate_charge.merchant_fees) || 0 : null,
        convenienceFee: kiosk.rate_charge ? parseFloat(kiosk.rate_charge.convenience_fee) || 0 : null,
      };

      console.log('KIOSK INFO:', kioskInfo);

      setKiosk(kioskInfo);
      setKioskToken(token);
    } catch (error) {
      console.error('KIOSK INITIALIZATION ERROR:', error);

      setKiosk(null);
      setKioskError(
        'Unable to identify this kiosk. Please scan the correct QR code.',
      );
    } finally {
      setIsInitializingKiosk(false);
    }
  };

  initializeKiosk();
}, [searchParams, setKiosk]);

  const handleFileAccepted = (file: File) => {
    stageFile(file);
  };
  

  if (isInitializingKiosk) {
    return (
      <AppLayout title="File Upload" currentStep={1}>
        <section className="bg-surface-lowest rounded-4 shadow-kiosk-sm p-5 d-flex flex-column align-items-center justify-content-center text-center min-h-[300px]">
          <div
            className="spinner-border text-primary mb-3"
            role="status"
            aria-label="Initializing kiosk"
          />
          <p className="text-body-lg text-on-surface mb-1">
            Initializing kiosk...
          </p>
          <p className="text-body-sm text-on-surface-variant mb-0">
            Please wait while we connect this session to the kiosk.
          </p>
        </section>
      </AppLayout>
    );
  }

  if (kioskError || !kiosk) {
    return (
      <AppLayout title="File Upload" currentStep={1}>
        <section className="bg-surface-lowest rounded-4 shadow-kiosk-sm p-5 d-flex flex-column align-items-center justify-content-center text-center min-h-[300px]">
          <i
            className="bi bi-exclamation-triangle text-danger mb-3"
            style={{ fontSize: 40 }}
          />
          <p className="text-body-lg text-on-surface mb-1">
            Kiosk initialization failed
          </p>
          <p className="text-body-sm text-on-surface-variant mb-0">
            {kioskError}
          </p>
        </section>
      </AppLayout>
    );
  }

  const canContinue =
    Boolean(kiosk) &&
    Boolean(uploadedFile) &&
    !isDetectingPageCount; 

  const bwRate = kiosk.bwRatePerPage;
  const pageCount = pageInfo.pageCount ?? 0;
  const estimatedTotal = pageCount * Number(bwRate);

  const stagedCard = uploadedFile ? (
    <StagedFileCard
      file={uploadedFile}
      pageCount={pageInfo.pageCount}
      isDetecting={isDetectingPageCount}
      onReplace={clear}
      onRemove={clear}
    />
  ) : null;

  return (
    <AppLayout title="File Upload" currentStep={1}>
      <Row className="g-3 g-lg-4">
        <Col xs={12} lg={7}>
          <UploadDropzone
            onFileAccepted={handleFileAccepted}
            disabled={isDetectingPageCount}
          />

          <div className="d-lg-none">{stagedCard}</div>

          <PrivacyNotice />
        </Col>

        <Col xs={12} lg={5} className="d-none d-lg-block">
          {stagedCard ?? (
            <section className="bg-surface-lowest rounded-4 shadow-kiosk-sm p-4 d-flex flex-column align-items-center text-center justify-content-center h-100 min-h-[220px]">
              <i
                className="bi bi-file-earmark-arrow-up text-on-surface-variant mb-2"
                style={{ fontSize: 32 }}
              />
              <p className="text-body-sm text-on-surface-variant mb-0">
                Your staged document will appear here once uploaded.
              </p>
            </section>
          )}
        </Col>
      </Row> 

      <StickyFooter>
        <button
          type="button"
          className="btn btn-primary w-100 tap-target active-press d-flex align-items-center justify-content-center gap-2 text-label-lg"
          style={{ height: 52 }}
          disabled={!canContinue}
          onClick={() => navigate(ROUTES.options)}
        >
          <span>Continue to Options</span>
          <i className="bi bi-arrow-right" />
        </button>

        <div className="d-flex justify-content-between pt-2 text-label-sm text-on-surface-variant">
          <span>Est. total: {pageCount || '—'} pages</span>
          <span>
            {bwRate != null ? (
              <>
                Standard Rate: ₹{bwRate}/page
                {pageCount > 0 && ` • ₹${estimatedTotal}`}
              </>
            ) : (
              'Rate unavailable for this kiosk'
            )}
          </span>
        </div>
      </StickyFooter>
    </AppLayout>
  );
}